<template>
    <div class="inv">
        <UserTopNav :active="'projects'" />

        <div class="inv-container">
            <!-- ═══ Back link ═══ -->
            <router-link
                :to="{
                    name: 'ProjectDetails',
                    params: {
                        projectId: currentProjectId,
                        projectSlug: currentProjectSlug,
                    },
                }"
                class="inv-back"
            >
                <font-awesome-icon icon="fa-solid fa-arrow-left" />
                <span>Inapoi la Proiect</span>
            </router-link>

            <!-- ═══ Header ═══ -->
            <div class="inv-header">
                <div class="inv-header__info">
                    <h1 class="inv-header__name">{{ currentProjectName || '...' }}</h1>
                    <p class="inv-header__sub">Gestionare invitați</p>
                </div>
                <div class="inv-header__actions" v-if="scopedProjectData">
                    <router-link
                        class="inv-btn"
                        :to="{
                            name: 'ProjectDetailsStats',
                            params: {
                                projectId: currentProjectId,
                                projectSlug: currentProjectSlug,
                            },
                        }"
                    >
                        <font-awesome-icon icon="fa-solid fa-chart-column" />
                        <span>Statistici</span>
                    </router-link>
                    <router-link
                        class="inv-btn inv-btn--ghost"
                        :to="{
                            name: 'ProjectDetailsConfig',
                            params: {
                                projectId: currentProjectId,
                                projectSlug: currentProjectSlug,
                            },
                        }"
                    >
                        <font-awesome-icon icon="fa-solid fa-sliders" />
                        <span>Setari proiect</span>
                    </router-link>
                </div>
            </div>

            <!-- ═══ Info card ═══ -->
            <section class="inv-card inv-card--info">
                <div class="inv-card__header inv-card__header--clickable" @click="infoBoxOpen = !infoBoxOpen">
                    <div class="inv-card__icon inv-card__icon--blue">
                        <font-awesome-icon icon="fa-solid fa-circle-info" />
                    </div>
                    <div class="inv-card__header-text">
                        <h2 class="inv-card__title">Cum funcționează invitațiile individuale</h2>
                    </div>
                    <font-awesome-icon
                        :icon="infoBoxOpen ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"
                        class="inv-card__toggle"
                    />
                </div>
                <div v-if="infoBoxOpen" class="inv-card__body">
                    <ul class="inv-tips">
                        <li>Fiecare invitat poate trimite <strong>un singur răspuns</strong> prin link-ul personal.</li>
                        <li>Dacă un invitat vrea să își modifice răspunsul, poți <strong>debloca formularul</strong> <font-awesome-icon icon="fa-solid fa-lock-open" class="text-warning" /> pentru a-i permite să retrimită.</li>
                        <li>Poți adăuga manual un răspuns în numele unui invitat (ex: confirmare telefonică) folosind butonul <strong>Răspuns manual</strong> <font-awesome-icon icon="fa-solid fa-clipboard-check" class="text-primary" />.</li>
                        <li><strong>Nu distribui link-ul unui invitat altei persoane</strong> — răspunsul va fi asociat invitatului original.</li>
                    </ul>
                </div>
            </section>

            <!-- ═══ Add participant card ═══ -->
            <section class="inv-card">
                <div class="inv-card__header">
                    <div class="inv-card__icon inv-card__icon--green">
                        <font-awesome-icon icon="fa-solid fa-user-plus" />
                    </div>
                    <div>
                        <h2 class="inv-card__title">Adaugă invitați</h2>
                        <p class="inv-card__desc">Completează datele și adaugă invitați în lista ta.</p>
                    </div>
                </div>
                <div class="inv-card__body">
                    <AddParticipantFormInline :projectId="currentProjectId" />
                </div>
            </section>

            <!-- ═══ Participants list card ═══ -->
            <section class="inv-card">
                <div class="inv-card__header">
                    <div class="inv-card__icon inv-card__icon--slate">
                        <font-awesome-icon icon="fa-solid fa-table-list" />
                    </div>
                    <div>
                        <h2 class="inv-card__title">Lista invitați</h2>
                        <p class="inv-card__desc" v-if="scopedProjectData">
                            {{ scopedProjectData.participants ? scopedProjectData.participants.length : 0 }} invitați adăugați
                        </p>
                    </div>
                </div>
                <div class="inv-card__body">
                    <GetParticipantsList :projectId="currentProjectId" />
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import AddParticipantFormInline from '@/components/AddParticipantFormInline.vue';
import GetParticipantsList from '@/components/GetParticipantsList.vue';
import UserTopNav from '@/components/UserTopNav.vue';
import { mapGetters } from 'vuex';

export default {
    name: 'ProjectDetailsInvitations',
    props: {
        projectId: {},
        projectName: {
            type: String,
        },
        projectSlug: {
            type: String,
        },
    },
    components: {
        AddParticipantFormInline,
        GetParticipantsList,
        UserTopNav,
    },
    data() {
        return {
            currentProjectId: null,
            infoBoxOpen: false,
        };
    },
    created() {
        this.currentProjectId = this.$route.params.projectId;
    },
    mounted() {
        if (
            !this.scopedProjectData ||
            this.scopedProjectData.projectId != this.$route.params.projectId
        ) {
            const projectId = this.$route.params.projectId;
            const options = {};
            this.$store.dispatch('fetchProjectUsers', {
                projectId,
                options,
                participants: true,
            });
        }
    },
    computed: {
        ...mapGetters([
            'projects',
            'plan',
            'loading',
            'scopedProjectData',
            'stats',
        ]),
        currentProjectName() {
            if (this.scopedProjectData && this.scopedProjectData.project_name) {
                return this.scopedProjectData.project_name;
            }
            return this.$route.params.projectSlug || '...';
        },
        currentProjectSlug() {
            if (this.scopedProjectData && this.scopedProjectData.project_slug) {
                return this.scopedProjectData.project_slug;
            }
            return this.$route.params.projectSlug || '';
        },
    },
};
</script>

<style scoped>
/* ═══════════════════════════════════════════════
   Layout — matches ProjectDetailsStats
   ═══════════════════════════════════════════════ */
.inv { min-height: 100vh; background: #f8fafc; }

.inv-container {
    max-width: 1280px;
    width: 100%;
    margin: 0 auto;
    padding: 0 20px 60px;
    text-align: left;
}

/* ── Back link ── */
.inv-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #64748b;
    text-decoration: none;
    padding: 24px 0 16px;
    transition: color 0.15s;
}
.inv-back:hover { color: #0f172a; }

/* ── Header ── */
.inv-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 28px;
}
.inv-header__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    padding-top: 4px;
}
.inv-header__name {
    font-size: 28px;
    font-weight: 800;
    color: #0f172a;
    margin: 0 0 2px;
    line-height: 1.2;
}
.inv-header__sub {
    font-size: 14px;
    color: #64748b;
    margin: 0;
}

/* ── Buttons ── */
.inv-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px 20px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 700;
    border: none;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.15s;
    white-space: nowrap;
    background: #0f172a;
    color: #fff;
}
.inv-btn:hover {
    background: #1e293b;
    transform: translateY(-1px);
    color: #fff;
}
.inv-btn--ghost {
    background: #f1f5f9;
    color: #475569;
}
.inv-btn--ghost:hover {
    background: #e2e8f0;
    color: #0f172a;
}

/* ═══════════════════════════════════════════════
   Card (shared pattern)
   ═══════════════════════════════════════════════ */
.inv-card {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    padding: 28px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
    margin-bottom: 16px;
}
.inv-card--info {
    border-color: #bfdbfe;
    background: linear-gradient(135deg, #f0f7ff 0%, #ffffff 100%);
}
.inv-card__header {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 24px;
    padding-bottom: 20px;
    border-bottom: 1px solid #f1f5f9;
}
.inv-card__header--clickable {
    cursor: pointer;
    user-select: none;
    transition: opacity 0.15s;
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
}
.inv-card__header--clickable:hover { opacity: 0.75; }

.inv-card--info .inv-card__body {
    padding-top: 20px;
    border-top: 1px solid #dbeafe;
    margin-top: 20px;
}

.inv-card__icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
}
.inv-card__icon--blue   { background: #dbeafe; color: #2563eb; }
.inv-card__icon--green  { background: #dcfce7; color: #16a34a; }
.inv-card__icon--slate  { background: #f1f5f9; color: #475569; }

.inv-card__header-text {
    flex: 1;
    min-width: 0;
}
.inv-card__title {
    font-size: 17px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 2px;
}
.inv-card__desc {
    font-size: 13px;
    color: #64748b;
    margin: 0;
    line-height: 1.4;
}
.inv-card__toggle {
    color: #94a3b8;
    font-size: 13px;
    flex-shrink: 0;
    transition: color 0.15s;
}
.inv-card__body {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

/* ── Info tips list ── */
.inv-tips {
    margin: 0;
    padding-left: 20px;
    list-style: none;
}
.inv-tips li {
    position: relative;
    font-size: 13px;
    color: #334155;
    line-height: 1.8;
    padding-left: 4px;
}
.inv-tips li::before {
    content: '•';
    position: absolute;
    left: -16px;
    color: #3b82f6;
    font-weight: 700;
}
.inv-tips li strong {
    color: #0f172a;
}

/* ═══════════════════════════════════════════════
   Mobile
   ═══════════════════════════════════════════════ */
@media (max-width: 768px) {
    .inv-container { padding: 0 16px 40px; }
    .inv-header { flex-direction: column; gap: 12px; }
    .inv-header__actions { width: 100%; }
    .inv-btn { padding: 8px 14px; font-size: 12px; }
    .inv-header__name { font-size: 22px; }
    .inv-card { padding: 20px 18px; border-radius: 14px; }
    .inv-card__header { gap: 12px; margin-bottom: 20px; padding-bottom: 16px; }
    .inv-card__icon { width: 38px; height: 38px; font-size: 16px; }
}
</style>
